import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAllTimeOffs, useApproveTimeOff, useRejectTimeOff, useAdminCancelTimeOff, useBulkSubmitTimeOffs, useBulkApproveTimeOffs, useBulkRejectTimeOffs, useBulkCancelTimeOffs, useSubmitTimeOff } from '../../hooks/useTimeOff';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { RejectModal } from '../../components/workflow';
import { TimeOffCalendar } from '../../components/time-off/TimeOffCalendar';
// MKS-361 — the Coverage column and its editor.
import TimeOffCoverageCell from '../../components/time-off/TimeOffCoverageCell';
import EditSubstitutesModal from '../../components/time-off/EditSubstitutesModal';
import { usePendingCoverage } from '../../hooks/useTimeOffCoverage';
import { useFlag } from '../../services/flagsContext';
import type { TimeOff } from '../../services/api/timeOff';
import { useTopScrollbarSync } from '../../hooks/useTopScrollbarSync';
import { formatDisplayDate } from '../../utils/format';

function formatDate(dateString: string | null): string {
  return formatDisplayDate(dateString);
}

function getUserName(user: TimeOff['user']): string {
  if (!user) return 'Unknown';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
}

export function PendingTimeOffsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: timeOffs, isLoading, error } = useAllTimeOffs();
  const approveMutation = useApproveTimeOff();
  const rejectMutation = useRejectTimeOff();
  const adminCancelMutation = useAdminCancelTimeOff();
  const bulkApproveMutation = useBulkApproveTimeOffs();
  const bulkRejectMutation = useBulkRejectTimeOffs();
  const bulkSubmitMutation = useBulkSubmitTimeOffs();
  const bulkCancelMutation = useBulkCancelTimeOffs();
  const submitMutation = useSubmitTimeOff();

  // MKS-361 — substitutes and overlap warnings for the whole queue, fetched
  // alongside the list rather than folded into it: the warnings are
  // recomputed on every read, and only this page needs them.
  const substitutesOn = useFlag('mks_361_time_off_substitutes');
  const { data: coverage } = usePendingCoverage(substitutesOn);
  const coverageById = useMemo(
    () => new Map((coverage ?? []).map((c) => [c.timeOffId, c] as const)),
    [coverage],
  );
  const [editingCoverageFor, setEditingCoverageFor] = useState<TimeOff | null>(null);

  // Pagination
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 50;

  const setPage = useCallback((page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', String(page));
      }
      return newParams;
    });
  }, [setSearchParams]);

  // Single item modals
  const [selectedTimeOff, setSelectedTimeOff] = useState<TimeOff | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [showBulkSubmitModal, setShowBulkSubmitModal] = useState(false);
  const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('submitted');
  const [nameFilter, setNameFilter] = useState('');
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const [calendarHighlightId, setCalendarHighlightId] = useState<number | null>(null);
  const [calendarNavigateTo, setCalendarNavigateTo] = useState<{ month: number; year: number } | null>(null);

  // F23-004: track outstanding timeouts so we cancel them on unmount. Without
  // this the 100ms scroll + two 3s highlight-clears fire `setState` after the
  // page navigates away, triggering React "update on unmounted component"
  // warnings (and StrictMode double-invoke duplicate scrolls).
  const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const scheduleTimeout = useCallback((fn: () => void, ms: number) => {
    const handle = setTimeout(() => {
      pendingTimeoutsRef.current.delete(handle);
      fn();
    }, ms);
    pendingTimeoutsRef.current.add(handle);
    return handle;
  }, []);
  useEffect(() => {
    const pending = pendingTimeoutsRef.current;
    return () => {
      pending.forEach((h) => clearTimeout(h));
      pending.clear();
    };
  }, []);

  // Filter and sort time offs (must be before any early returns)
  const filteredTimeOffs = useMemo(() => {
    let result = [...(timeOffs || [])];

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter((t) => t.state === statusFilter);
    }

    // Filter by name
    if (nameFilter.trim()) {
      const search = nameFilter.toLowerCase();
      result = result.filter((t) => {
        const fullName = `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.toLowerCase();
        const email = (t.user?.email || '').toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
    }

    // Sort by starting date descending (most recent first)
    result.sort((a, b) => {
      return new Date(b.dateFrom || 0).getTime() - new Date(a.dateFrom || 0).getTime();
    });

    return result;
  }, [timeOffs, statusFilter, nameFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTimeOffs.length / pageSize);
  const paginatedTimeOffs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTimeOffs.slice(start, start + pageSize);
  }, [filteredTimeOffs, currentPage, pageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage > 1 && currentPage > totalPages) {
      setPage(1);
    }
  }, [currentPage, totalPages, setPage]);

  // Get selected items that can be approved/rejected/cancelled
  const selectedItems = useMemo(() => {
    return filteredTimeOffs.filter((t) => selectedIds.has(t.id));
  }, [filteredTimeOffs, selectedIds]);

  const submittableItems = useMemo(() => {
    return selectedItems.filter((t) => t.state === 'open');
  }, [selectedItems]);

  const approvableItems = useMemo(() => {
    return selectedItems.filter((t) => t.state === 'submitted');
  }, [selectedItems]);

  const cancellableItems = useMemo(() => {
    return selectedItems.filter((t) => ['open', 'submitted', 'approved'].includes(t.state));
  }, [selectedItems]);

  // Selection handlers - only select items on current page
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(paginatedTimeOffs.map((t) => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  }, [paginatedTimeOffs]);

  const handleSelectOne = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  // Single item handlers
  const handleApproveClick = (timeOff: TimeOff) => {
    setSelectedTimeOff(timeOff);
    setShowApproveModal(true);
  };

  const handleRejectClick = (timeOff: TimeOff) => {
    setSelectedTimeOff(timeOff);
    setShowRejectModal(true);
  };

  const handleSubmitClick = (timeOff: TimeOff) => {
    setSelectedTimeOff(timeOff);
    setShowSubmitModal(true);
  };

  const handleCancelClick = (timeOff: TimeOff) => {
    setSelectedTimeOff(timeOff);
    setShowCancelModal(true);
  };

  const handleApprove = () => {
    if (selectedTimeOff) {
      approveMutation.mutate(selectedTimeOff.id, {
        onSuccess: () => {
          setShowApproveModal(false);
          setSelectedTimeOff(null);
        },
      });
    }
  };

  const handleReject = (reason: string) => {
    if (selectedTimeOff) {
      rejectMutation.mutate({ id: selectedTimeOff.id, reason }, {
        onSuccess: () => {
          setShowRejectModal(false);
          setSelectedTimeOff(null);
        },
      });
    }
  };

  const handleSubmit = () => {
    if (selectedTimeOff) {
      submitMutation.mutate(selectedTimeOff.id, {
        onSuccess: () => {
          setShowSubmitModal(false);
          setSelectedTimeOff(null);
        },
      });
    }
  };

  const handleCancel = () => {
    if (selectedTimeOff) {
      adminCancelMutation.mutate(selectedTimeOff.id, {
        onSuccess: () => {
          setShowCancelModal(false);
          setSelectedTimeOff(null);
        },
      });
    }
  };

  // Bulk action handlers
  const handleBulkSubmit = () => {
    const ids = submittableItems.map((item) => item.id);
    bulkSubmitMutation.mutate(ids, {
      onSuccess: () => {
        setSelectedIds(new Set());
        setShowBulkSubmitModal(false);
      },
    });
  };

  const handleBulkApprove = () => {
    const ids = approvableItems.map((item) => item.id);
    bulkApproveMutation.mutate(ids, {
      onSuccess: () => {
        setSelectedIds(new Set());
        setShowBulkApproveModal(false);
      },
    });
  };

  const handleBulkReject = (reason: string) => {
    const ids = approvableItems.map((item) => item.id);
    bulkRejectMutation.mutate({ ids, reason }, {
      onSuccess: () => {
        setSelectedIds(new Set());
        setShowBulkRejectModal(false);
      },
    });
  };

  const handleBulkCancel = () => {
    const ids = cancellableItems.map((item) => item.id);
    bulkCancelMutation.mutate(ids, {
      onSuccess: () => {
        setSelectedIds(new Set());
        setShowBulkCancelModal(false);
      },
    });
  };

  const handleTimeOffClick = useCallback((timeOffId: number) => {
    // Clear all filters to ensure the time-off is visible
    setStatusFilter('all');
    setNameFilter('');
    // Highlight the time-off
    setHighlightedId(timeOffId);
    // Scroll to the time-off after a brief delay to allow for filtering
    scheduleTimeout(() => {
      const element = document.getElementById(`time-off-${timeOffId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    // Clear highlight after 3 seconds
    scheduleTimeout(() => {
      setHighlightedId(null);
    }, 3000);
  }, [scheduleTimeout]);

  const handleRowClick = useCallback((timeOff: TimeOff) => {
    if (!timeOff.dateFrom) return;
    const date = new Date(timeOff.dateFrom);
    setCalendarNavigateTo({ month: date.getMonth(), year: date.getFullYear() });
    setCalendarHighlightId(timeOff.id);
    // Scroll calendar into view
    document.getElementById('time-off-calendar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Clear highlight after 3 seconds
    scheduleTimeout(() => setCalendarHighlightId(null), 3000);
  }, [scheduleTimeout]);

  // F21-006: scroll refs + overflow tracking extracted to useTopScrollbarSync
  const { topScrollRef, bottomScrollRef, topScrollContentRef, hasOverflow } =
    useTopScrollbarSync([filteredTimeOffs]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load time-off requests: {(error as Error).message}</p>
      </div>
    );
  }

  const allSelected = paginatedTimeOffs.length > 0 && paginatedTimeOffs.every((t) => selectedIds.has(t.id));
  const someSelected = selectedIds.size > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Time Offs</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage time-off requests for your team
        </p>
      </div>

      {/* Calendar View */}
      {timeOffs && timeOffs.length > 0 && (
        <div id="time-off-calendar">
          <TimeOffCalendar
            timeOffs={timeOffs}
            onTimeOffClick={handleTimeOffClick}
            showStatusForAll
            navigateToMonth={calendarNavigateTo}
            highlightedTimeOffId={calendarHighlightId}
          />
        </div>
      )}

      {/* Top Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
            <span className="font-medium">{Math.min(currentPage * pageSize, filteredTimeOffs.length)}</span> of{' '}
            <span className="font-medium">{filteredTimeOffs.length}</span> time offs
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={`top-page-${pageNum}`}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="flex items-center gap-2 justify-end">
          <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
          {submittableItems.length > 0 && (
            <button
              onClick={() => setShowBulkSubmitModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              Submit ({submittableItems.length})
            </button>
          )}
          {approvableItems.length > 0 && (
            <button
              onClick={() => setShowBulkApproveModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              Approve ({approvableItems.length})
            </button>
          )}
          {approvableItems.length > 0 && (
            <button
              onClick={() => setShowBulkRejectModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700"
            >
              Reject ({approvableItems.length})
            </button>
          )}
          {cancellableItems.length > 0 && (
            <button
              onClick={() => setShowBulkCancelModal(true)}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Delete ({cancellableItems.length})
            </button>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 relative">
        {!paginatedTimeOffs || paginatedTimeOffs.length === 0 ? (
          <div className="text-center py-12">
            <CheckIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No time-off requests</h3>
            <p className="mt-1 text-sm text-gray-500">
              No time-off requests found.
            </p>
          </div>
        ) : (
          <>
            {hasOverflow && (
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-xs text-blue-700 font-medium rounded-t-xl">
                ⟵ Scroll the table left and right to see every column ⟶
              </div>
            )}
            <style>{`
              .table-scroll-container {
                overflow-x: scroll !important;
                width: 100%;
                display: block;
              }
              .table-scroll-container::-webkit-scrollbar {
                height: 20px;
                background: #F3F4F6;
              }
              .table-scroll-container::-webkit-scrollbar-track {
                background: #F3F4F6;
                border-top: 1px solid #E5E7EB;
              }
              .table-scroll-container::-webkit-scrollbar-thumb {
                background: #9CA3AF;
                border-radius: 10px;
                border: 4px solid #F3F4F6;
              }
              .table-scroll-container::-webkit-scrollbar-thumb:hover {
                background: #6B7280;
              }
              .top-scroll-container {
                overflow-x: scroll !important;
                overflow-y: hidden;
                width: 100%;
                display: block;
              }
              .top-scroll-container::-webkit-scrollbar {
                height: 20px;
                background: #F3F4F6;
              }
              .top-scroll-container::-webkit-scrollbar-track {
                background: #F3F4F6;
              }
              .top-scroll-container::-webkit-scrollbar-thumb {
                background: #9CA3AF;
                border-radius: 10px;
                border: 4px solid #F3F4F6;
              }
              .top-scroll-container::-webkit-scrollbar-thumb:hover {
                background: #6B7280;
              }
            `}</style>
            {/* Top scrollbar - only show when overflowing */}
            {hasOverflow && (
              <div ref={topScrollRef} className="top-scroll-container border-b border-gray-200">
                <div ref={topScrollContentRef} style={{ height: '1px' }}></div>
              </div>
            )}
            {/* Bottom scrollbar with table */}
            <div ref={bottomScrollRef} className={`table-scroll-container ${!hasOverflow ? 'rounded-t-xl' : ''} rounded-b-xl`}>
              <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  {substitutesOn && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coverage
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTimeOffs.map((timeOff) => (
                  <tr
                    key={timeOff.id}
                    id={`time-off-${timeOff.id}`}
                    onClick={() => handleRowClick(timeOff)}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                      highlightedId === timeOff.id
                        ? 'bg-yellow-100 ring-2 ring-yellow-400'
                        : selectedIds.has(timeOff.id)
                          ? 'bg-blue-50'
                          : ''
                    }`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(timeOff.id)}
                        onChange={(e) => handleSelectOne(timeOff.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <span className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                            {timeOff.user?.firstName?.[0] || timeOff.user?.email[0]?.toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {getUserName(timeOff.user)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {timeOff.user?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {timeOff.timeOffType?.name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(timeOff.dateFrom)} - {formatDate(timeOff.dateTo)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {timeOff.totalDays} day{timeOff.totalDays !== 1 ? 's' : ''}
                        <span className="text-gray-500 ml-1">({timeOff.totalHours}h)</span>
                      </div>
                    </td>
                    {substitutesOn && (
                      <td className="px-6 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                        <TimeOffCoverageCell
                          coverage={coverageById.get(timeOff.id)}
                          onEdit={() => setEditingCoverageFor(timeOff)}
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge state={timeOff.state} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {timeOff.state === 'open' && (
                          <button
                            onClick={() => handleSubmitClick(timeOff)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium px-2"
                            title="Submit for approval"
                          >
                            Submit
                          </button>
                        )}
                        {timeOff.state === 'submitted' && (
                          <>
                            <button
                              onClick={() => handleApproveClick(timeOff)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg"
                              title="Approve"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleRejectClick(timeOff)}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                              title="Reject"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                        {['open', 'submitted', 'approved'].includes(timeOff.state) && (
                          <button
                            onClick={() => handleCancelClick(timeOff)}
                            className="text-sm text-red-600 hover:text-red-800 font-medium px-2"
                            title="Delete this time off"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
            <span className="font-medium">{Math.min(currentPage * pageSize, filteredTimeOffs.length)}</span> of{' '}
            <span className="font-medium">{filteredTimeOffs.length}</span> time offs
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={`bottom-page-${pageNum}`}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                      currentPage === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Single Approve Modal */}
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setSelectedTimeOff(null);
        }}
        onConfirm={handleApprove}
        title="Approve Time Off Request"
        message={`Approve ${getUserName(selectedTimeOff?.user ?? null)}'s time off request for ${selectedTimeOff?.totalDays} day(s)?`}
        confirmText="Approve"
        variant="primary"
        isLoading={approveMutation.isPending}
      />

      {/* Single Reject Modal */}
      <RejectModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedTimeOff(null);
        }}
        onConfirm={handleReject}
        isLoading={rejectMutation.isPending}
        employeeName={getUserName(selectedTimeOff?.user ?? null)}
      />

      {/* Single Submit Modal */}
      <ConfirmModal
        isOpen={showSubmitModal}
        onClose={() => {
          setShowSubmitModal(false);
          setSelectedTimeOff(null);
        }}
        onConfirm={handleSubmit}
        title="Submit Time Off Request"
        message={`Submit ${getUserName(selectedTimeOff?.user ?? null)}'s time off request for approval?`}
        confirmText="Submit"
        variant="primary"
        isLoading={submitMutation.isPending}
      />

      {/* Single Cancel Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setSelectedTimeOff(null);
        }}
        onConfirm={handleCancel}
        title="Delete Time Off"
        message={`Delete ${getUserName(selectedTimeOff?.user ?? null)}'s ${selectedTimeOff?.timeOffType?.name || 'time off'} from ${formatDisplayDate(selectedTimeOff?.dateFrom)} to ${formatDisplayDate(selectedTimeOff?.dateTo)}?`}
        confirmText="Delete"
        variant="danger"
        isLoading={adminCancelMutation.isPending}
      />

      {/* Bulk Submit Modal */}
      <ConfirmModal
        isOpen={showBulkSubmitModal}
        onClose={() => setShowBulkSubmitModal(false)}
        onConfirm={handleBulkSubmit}
        title="Bulk Submit Time Off Requests"
        message={`Submit ${submittableItems.length} time off request(s) for approval?`}
        confirmText="Submit All"
        variant="primary"
        isLoading={bulkSubmitMutation.isPending}
      />

      {/* Bulk Approve Modal */}
      <ConfirmModal
        isOpen={showBulkApproveModal}
        onClose={() => setShowBulkApproveModal(false)}
        onConfirm={handleBulkApprove}
        title="Bulk Approve Time Off Requests"
        message={`Approve ${approvableItems.length} time off request(s)?`}
        confirmText="Approve All"
        variant="primary"
        isLoading={bulkApproveMutation.isPending}
      />

      {/* Bulk Reject Modal */}
      <RejectModal
        isOpen={showBulkRejectModal}
        onClose={() => setShowBulkRejectModal(false)}
        onConfirm={handleBulkReject}
        isLoading={bulkRejectMutation.isPending}
        employeeName={`${approvableItems.length} employee(s)`}
      />

      {/* Bulk Cancel Modal */}
      <ConfirmModal
        isOpen={showBulkCancelModal}
        onClose={() => setShowBulkCancelModal(false)}
        onConfirm={handleBulkCancel}
        title="Bulk Delete Time Offs"
        message={`Delete ${cancellableItems.length} time off(s)? This action cannot be undone.`}
        confirmText="Delete All"
        variant="danger"
        isLoading={bulkCancelMutation.isPending}
      />

      {/* MKS-361 — the approver changing who covers. Mounted only while a row
          is being edited, so the modal's own coverage query is not fired for
          every row in the queue. */}
      {substitutesOn && editingCoverageFor && (
        <EditSubstitutesModal
          isOpen
          timeOffId={editingCoverageFor.id}
          requesterName={getUserName(editingCoverageFor.user)}
          onClose={() => setEditingCoverageFor(null)}
        />
      )}
    </div>
  );
}
